import React from 'react';
import {
  Box,
  BoxProps,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Spacer,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';

import { DateSelection, Field } from '../../types/shared/Vendor';
import { Market } from '../../types/shared/Market';
import formatDate from '../../core/support/formatDate';
import { DateStatus } from '../../runtypes/VendorRuntype';

type Props = {
  fieldValue: Field | undefined;
  markets: Array<Market>;
};

export function MarketsView(props: Props) {
  const { fieldValue } = props;
  const markets = props.markets.filter((market) => !market.hidden);
  const children = markets.map((market) => {
    const dates = fieldValue?.dateSelectionByMarket?.[market.id] ?? {};
    return renderMarket(market, dates);
  });
  const hasAny = children.some((el) => el != null);
  if (!hasAny) {
    return (
      <VStack p={2}>
        <Text color="gray.400">{t('(No market dates to display)')}</Text>
      </VStack>
    );
  }
  return (
    <VStack align="stretch" spacing={0}>
      <HStack
        py={2}
        px={4}
        borderWidth={1}
        borderColor="gray.200"
        borderRadius={5}
        my={2}
      >
        <LegendItem color="green.400" text={t('Approved')} />
        <LegendItem color="blue.400" text={t('Waitlisted')} />
        <LegendItem color="orange.400" text={t('Requested')} />
      </HStack>
      <VStack align="stretch" spacing={0} gap={5}>
        {children}
      </VStack>
    </VStack>
  );
}

// This is intentionally not a React component so we can filter for nulls
function renderMarket(market: Market, dates: DateSelection) {
  const marketName = market.name;
  const displayableDates = market.dates.filter((date) =>
    isDisplayable(dates[date]),
  );
  const count = displayableDates.length;
  if (count === 0) {
    return null;
  }
  return (
    <VStack key={market.id} align="stretch">
      <HStack>
        <Text fontWeight="medium">{marketName}</Text>
        <MarketDateSummary
          market={market}
          dates={dates}
          text={count === 1 ? t('(1 date)') : t('({count} dates)', { count })}
        />
      </HStack>
      <HStack flexWrap="wrap" spacing={0} gap={2}>
        {market.dates.map((date) => (
          <Date key={date} date={date} status={dates[date]} />
        ))}
      </HStack>
    </VStack>
  );
}

function Date(props: { date: string; status: DateStatus | undefined }) {
  const { date, status } = props;
  const formattedDate = formatDate(date, { fromUTC: true });
  const display = getDisplayStyle(status);
  if (!display) {
    return null;
  }
  const { borderColor, backgroundColor } = display;
  return (
    <Tooltip label={getLabelForDateStatus(status)} placement="top" hasArrow>
      <Text
        paddingX={3}
        paddingY={1}
        minW={160}
        borderLeftWidth={3}
        borderLeftColor={borderColor}
        backgroundColor={backgroundColor}
        cursor="default"
      >
        {formattedDate}
      </Text>
    </Tooltip>
  );
}

function getDisplayStyle(status: DateStatus | undefined) {
  switch (status) {
    case 'REQUESTED': {
      return {
        borderColor: 'orange.400',
        backgroundColor: 'orange.50',
      };
    }
    case 'APPROVED': {
      return {
        borderColor: 'green.400',
        backgroundColor: 'green.50',
      };
    }
    case 'WAITLISTED': {
      return {
        borderColor: 'blue.400',
        backgroundColor: 'blue.50',
      };
    }
    default: {
      return null;
    }
  }
}

// Note: Duplicate logic here and in DateStatusText and ChangeRequestReviewMarketsView
function getLabelForDateStatus(dateStatus: DateStatus | undefined) {
  switch (dateStatus) {
    case 'REQUESTED': {
      return t('Requested');
    }
    case 'APPROVED': {
      return t('Approved');
    }
    case 'WAITLISTED': {
      return t('Waitlisted');
    }
    default: {
      return t('Not Attending');
    }
  }
}

function MarketDateSummary(props: {
  market: Market;
  dates: DateSelection;
  text: string;
}) {
  const { market, dates, text } = props;
  const keys = Object.keys(dates);
  const counts = {
    approved: keys.filter((date) => dates[date] === 'APPROVED').length,
    waitlisted: keys.filter((date) => dates[date] === 'WAITLISTED').length,
    requested: keys.filter((date) => dates[date] === 'REQUESTED').length,
  };
  return (
    <Popover trigger="hover">
      <PopoverTrigger>
        <Text
          color="gray.400"
          textDecorationLine="underline"
          textDecorationStyle="dashed"
          cursor="pointer"
        >
          {text}
        </Text>
      </PopoverTrigger>
      <Portal>
        <PopoverContent zIndex={1}>
          <PopoverArrow />
          <PopoverHeader>
            <VStack align="stretch">
              <Text fontWeight="medium">{market.name}</Text>
              {market.description ? (
                <Text color="gray.400">{market.description}</Text>
              ) : null}
            </VStack>
          </PopoverHeader>
          <PopoverBody>
            <VStack align="stretch">
              <SummaryItem
                color="green.400"
                label={t('Approved')}
                dateCount={counts.approved}
              />
              <SummaryItem
                color="blue.400"
                label={t('Waitlisted')}
                dateCount={counts.waitlisted}
              />
              <SummaryItem
                color="orange.400"
                label={t('Requested')}
                dateCount={counts.requested}
              />
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}

function SummaryItem(props: {
  color: BoxProps['backgroundColor'];
  label: string;
  dateCount: number;
}) {
  const { color, label, dateCount: count } = props;
  return (
    <HStack flex={1} align="center" spacing={3}>
      <Box width={3} height={3} backgroundColor={color} />
      <Text>{label}</Text>
      <Spacer />
      <Text color="gray.400">{t('{count} date(s)', { count })}</Text>
    </HStack>
  );
}

function LegendItem(props: {
  color: BoxProps['backgroundColor'];
  text: string;
}) {
  const { color, text } = props;
  return (
    <HStack flex={1} align="center" spacing={3}>
      <Box width={3} height={3} backgroundColor={color} />
      <Text>{text}</Text>
    </HStack>
  );
}

// TODO: Revisit this. It doesn't make a lot of sense in its current form.
function isDisplayable(status: DateStatus | undefined) {
  switch (status) {
    case 'REQUESTED':
    case 'APPROVED':
    case 'WAITLISTED': {
      return true;
    }
    default: {
      return false;
    }
  }
}
